import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './AddressModal.css';

interface Address {
  id: string;
  name?: string;
  street?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  postal_code?: string;
  phone?: string;
  is_default?: boolean;
  full_address?: string;
  address?: string;
  location?: string;
}

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAddress: (address: string) => void;
  currentAddress?: string;
}

const AddressModal: React.FC<AddressModalProps> = ({
  isOpen,
  onClose,
  onSelectAddress,
  currentAddress,
}) => {
  const { isAuthenticated, user: _user } = useAuth();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    phone: '',
    address_type: 'customer',
    country: 'India',
    is_default: false,
  });

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      loadAddresses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAuthenticated]);

  const loadAddresses = async () => {
    if (!isAuthenticated) {
      console.log('🛒 AddressModal: User not authenticated, skipping address load');
      return;
    }

    try {
      setLoading(true);
      setError('');
      console.log('🛒 AddressModal: Loading addresses for authenticated user...');
      
      const fetchedAddresses = await apiService.getUserAddresses();
      console.log('🛒 AddressModal: Fetched addresses:', fetchedAddresses);
      
      if (fetchedAddresses && Array.isArray(fetchedAddresses)) {
        setAddresses(fetchedAddresses);
      } else {
        console.warn('🛒 AddressModal: Invalid address data received, using empty array');
        setAddresses([]);
      }
    } catch (err: any) {
      console.error('❌ AddressModal: Error loading addresses:', err);
      console.error('❌ AddressModal: Error details:', {
        message: err?.message,
        status: err?.status,
        response: err?.response,
        stack: err?.stack
      });
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load addresses. Please try again.';
      if (err?.message) {
        if (err.message.includes('Network') || err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          errorMessage = 'Please log in to view your addresses.';
        } else if (err.message.includes('404')) {
          errorMessage = 'Address service not available. Please try again later.';
        } else if (err.message.includes('500')) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = err.message || errorMessage;
        }
      }
      
      setError(errorMessage);
      setAddresses([]); // Set empty array on error so user can still add addresses
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address: Address): string => {
    if (address.full_address) {
      return address.full_address;
    }
    
    let formatted = '';
    if (address.street || address.address_line1) {
      formatted = address.street || address.address_line1 || '';
      if (address.address_line2) {
        formatted += `, ${address.address_line2}`;
      }
      if (address.city) {
        formatted += `, ${address.city}`;
      }
      if (address.state) {
        formatted += `, ${address.state}`;
      }
      if (address.pincode || address.postal_code) {
        formatted += ` ${address.pincode || address.postal_code}`;
      }
    } else if (address.address) {
      formatted = address.address;
    } else if (address.location) {
      formatted = address.location;
    }
    
    return formatted || 'Address not specified';
  };

  const handleSelectAddress = (address: Address) => {
    const formatted = formatAddress(address);
    onSelectAddress(formatted);
    onClose();
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setFormData({
      name: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      phone: '',
      address_type: 'customer',
      country: 'India',
      is_default: false,
    });
    setShowAddForm(true);
  };

  const handleEdit = (address: Address) => {
    setEditingAddress(address);
    setFormData({
      name: address.name || '',
      address_line1: address.address_line1 || address.street || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || address.pincode || '',
      phone: address.phone || '',
      address_type: 'customer', // Always "customer" for Customer Web
      country: (address as any).country || 'India',
      is_default: address.is_default || false,
    });
    setShowAddForm(true);
  };

  const handleDelete = async (addressId: string) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      setLoading(true);
      await apiService.deleteAddress(addressId);
      await loadAddresses();
    } catch (err: any) {
      console.error('Error deleting address:', err);
      setError('Failed to delete address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (addressId: string) => {
    try {
      setLoading(true);
      await apiService.setDefaultAddress(addressId);
      await loadAddresses();
    } catch (err: any) {
      console.error('Error setting default address:', err);
      setError('Failed to set default address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check authentication first
    if (!isAuthenticated) {
      setError('Please log in to save addresses.');
      return;
    }
    
    if (!formData.address_line1.trim() || !formData.city.trim()) {
      setError('Please fill in required fields (Address and City)');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const addressData = {
        name: formData.name || 'Home',
        address_line1: formData.address_line1,
        address_line2: formData.address_line2,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postal_code,
        phone: formData.phone,
        address_type: formData.address_type,
        country: formData.country,
        is_default: formData.is_default,
      };

      if (editingAddress) {
        await apiService.updateAddress(editingAddress.id, addressData);
      } else {
        await apiService.createAddress(addressData);
      }

      await loadAddresses();
      setShowAddForm(false);
      setEditingAddress(null);
    } catch (err: any) {
      console.error('Error saving address:', err);
      console.error('Error details:', {
        message: err?.message,
        status: err?.status,
        response: err?.response,
        stack: err?.stack
      });
      
      // Provide more specific error messages
      let errorMessage = 'Failed to save address. Please try again.';
      
      if (err?.message) {
        if (err.message.includes('Network') || err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          errorMessage = 'Please log in to save addresses.';
        } else if (err.message.includes('400') || err.message.includes('Bad Request')) {
          errorMessage = 'Invalid address data. Please check all fields and try again.';
        } else if (err.message.includes('500') || err.message.includes('Internal Server Error')) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = err.message || errorMessage;
        }
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="address-modal-overlay" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Manage delivery addresses"
    >
      <div className="address-modal" onClick={(e) => e.stopPropagation()}>
        <div className="address-modal-header">
          <h3>Manage Addresses</h3>
          <button 
            className="address-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="address-modal-error">
            {error}
          </div>
        )}

        {!showAddForm ? (
          <>
            {loading && addresses.length === 0 ? (
              <div className="address-modal-loading">Loading addresses...</div>
            ) : addresses.length === 0 ? (
              <div className="address-modal-empty">
                <p>No addresses saved. Add your first address!</p>
              </div>
            ) : (
              <div className="address-modal-list">
                {addresses.map((address) => {
                  const formatted = formatAddress(address);
                  const isSelected = formatted === currentAddress;
                  
                  return (
                    <div 
                      key={address.id} 
                      className={`address-modal-item ${isSelected ? 'selected' : ''} ${address.is_default ? 'default' : ''}`}
                    >
                      <div 
                        className="address-modal-item-content"
                        onClick={() => handleSelectAddress(address)}
                      >
                        <div className="address-modal-item-header">
                          <span className="address-modal-item-icon">📍</span>
                          <div className="address-modal-item-info">
                            <div className="address-modal-item-label">
                              {address.name || 'Home'}
                              {address.is_default && (
                                <span className="address-modal-item-badge">Default</span>
                              )}
                            </div>
                            <div className="address-modal-item-value">{formatted}</div>
                            {address.phone && (
                              <div className="address-modal-item-phone">{address.phone}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="address-modal-item-actions">
                        {!address.is_default && (
                          <button
                            className="address-modal-action-btn"
                            onClick={() => handleSetDefault(address.id)}
                            title="Set as default"
                          >
                            Set Default
                          </button>
                        )}
                        <button
                          className="address-modal-action-btn"
                          onClick={() => handleEdit(address)}
                          title="Edit address"
                        >
                          Edit
                        </button>
                        <button
                          className="address-modal-action-btn delete"
                          onClick={() => handleDelete(address.id)}
                          title="Delete address"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="address-modal-footer">
              <button 
                className="address-modal-add-btn"
                onClick={handleAddNew}
                disabled={loading}
              >
                + Add New Address
              </button>
            </div>
          </>
        ) : (
          <form className="address-modal-form" onSubmit={handleSubmit}>
            <h4>{editingAddress ? 'Edit Address' : 'Add New Address'}</h4>
            
            <div className="address-form-group">
              <label htmlFor="address-name">Address Name (e.g., Home, Office)</label>
              <input
                id="address-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Home"
              />
            </div>

            <div className="address-form-group">
              <label htmlFor="address-line1">Address Line 1 *</label>
              <input
                id="address-line1"
                type="text"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                placeholder="Street address, apartment, suite"
                required
              />
            </div>

            <div className="address-form-group">
              <label htmlFor="address-line2">Address Line 2</label>
              <input
                id="address-line2"
                type="text"
                value={formData.address_line2}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                placeholder="Apartment, suite, unit, building, floor, etc."
              />
            </div>

            <div className="address-form-row">
              <div className="address-form-group">
                <label htmlFor="address-city">City *</label>
                <input
                  id="address-city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                  required
                />
              </div>

              <div className="address-form-group">
                <label htmlFor="address-state">State</label>
                <input
                  id="address-state"
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="State"
                />
              </div>
            </div>

            <div className="address-form-row">
              <div className="address-form-group">
                <label htmlFor="address-postal">Postal Code</label>
                <input
                  id="address-postal"
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  placeholder="Postal code"
                />
              </div>

              <div className="address-form-group">
                <label htmlFor="address-phone">Phone</label>
                <input
                  id="address-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
            </div>

            <div className="address-form-group">
              <label htmlFor="address-country">Country *</label>
              <input
                id="address-country"
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="Country"
                required
              />
            </div>

            <div className="address-form-group">
              <label className="address-form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                />
                Set as default address
              </label>
            </div>

            <div className="address-form-actions">
              <button
                type="button"
                className="address-form-cancel-btn"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingAddress(null);
                  setError('');
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="address-form-submit-btn"
                disabled={loading}
              >
                {loading ? 'Saving...' : editingAddress ? 'Update Address' : 'Save Address'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddressModal;

